/**
 * SMS conversation thread (/messages/:id) — READ side only (plan §9.2, §9.5, §9.6).
 *
 * There is NO composer: replies are not built. Where the composer will go, the page links to the
 * contact page, where the existing one-way "Send SMS" quick action still works.
 *
 * Read state (plan §5.2): every render of a thread is acknowledged with its server snapshot id.
 * `p_foreground` is true only for a load the user is looking at (visible AND focused); background
 * polls and refetches after an action pass false, so "Mark unread" is not undone by the refetch that
 * follows it — a reminder clears only on a later foreground open.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ackSmsConversation,
  getSmsConversationThread,
  isForegroundView,
  markSmsConversationNoReplyNeeded,
  markSmsConversationRead,
  markSmsConversationUnread,
  SmsConversationError,
  type SmsThread as SmsThreadData,
  type SmsThreadItem,
} from "../services/smsConversations.service";
import {
  formatSmsTime,
  SMS_ATTACHMENT_LINE,
  smsKeywordLabel,
  smsOutboundStateLabel,
} from "../utils/smsConversationFormat";
import SharedNumberChip from "../components/sms/SharedNumberChip";

const POLL_MS = 60_000;
const PAGE = 200;

type LoadMode = "initial" | "poll" | "action";

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function groupByDay(items: SmsThreadItem[]) {
  const groups: Array<{ key: string; label: string; items: SmsThreadItem[] }> =
    [];
  for (const it of items) {
    const k = dayKey(it.display_at);
    const last = groups[groups.length - 1];
    if (last && last.key === k) {
      last.items.push(it);
    } else {
      groups.push({ key: k, label: dayLabel(it.display_at), items: [it] });
    }
  }
  return groups;
}

const Message: React.FC<{ item: SmsThreadItem; suppressed: boolean }> = ({
  item,
  suppressed,
}) => {
  if (item.kind === "outbound") {
    const o = item.outbound;
    const time = new Date(item.display_at).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    return (
      <li className="flex justify-end">
        <div className="max-w-[85%] rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 sm:max-w-[70%]">
          <div className="text-xs font-medium text-gray-600">
            Sent by {o?.sent_by_name ?? "staff"} · {time}
          </div>
          {item.body === null ? (
            <p className="mt-1 text-sm italic text-gray-600">
              This message was removed under the retention policy.
            </p>
          ) : (
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-900">
              {item.body}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-600">
            {smsOutboundStateLabel(o?.state)}
            {o?.concerning ? ` · about ${o.concerning}` : ""} · from the contact
            page
          </p>
        </div>
      </li>
    );
  }
  const label = smsKeywordLabel(item.classification, suppressed);
  return (
    <li className="flex justify-start">
      <div className="max-w-[85%] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 sm:max-w-[70%]">
        <div className="text-xs font-medium text-gray-600">
          From this number ·{" "}
          {new Date(item.display_at).toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
          })}
          {item.needs_reply && (
            <span className="ml-2 rounded border border-red-300 bg-red-50 px-1 text-red-800">
              Needs reply
            </span>
          )}
        </div>
        {label && (
          <div className="mt-1 text-xs font-semibold text-amber-900">
            {label}
          </div>
        )}
        {item.body === null ? (
          <p className="mt-1 text-sm italic text-gray-600">
            This message was removed under the retention policy.
          </p>
        ) : (
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-900">
            {item.body}
          </p>
        )}
        {item.attachments > 0 && (
          <p className="mt-1 text-xs text-gray-600">{SMS_ATTACHMENT_LINE}</p>
        )}
      </div>
    </li>
  );
};

const SmsThread: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [thread, setThread] = useState<SmsThreadData | null>(null);
  const [state, setState] = useState<
    "loading" | "ready" | "error" | "not_found" | "withdrawn"
  >("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string>("");
  const [announce, setAnnounce] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const seenSeqs = useRef<Set<number> | null>(null);
  const loadingRef = useRef(false);

  const handleError = useCallback(
    (e: unknown): "stale" | "handled" => {
      const err =
        e instanceof SmsConversationError
          ? e
          : new SmsConversationError("other", String(e));
      if (err.kind === "stale") {
        return "stale";
      }
      if (err.kind === "not_found") {
        setState("not_found");
      } else if (err.kind === "disabled" || err.kind === "not_authorized") {
        void queryClient.invalidateQueries({
          queryKey: ["sms-conversations-available"],
        });
        setState("withdrawn");
      } else {
        setState((s) => (s === "ready" ? s : "error"));
      }
      return "handled";
    },
    [queryClient],
  );

  const load = useCallback(
    async (mode: LoadMode, retried = false): Promise<void> => {
      if (!id || loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      try {
        const t = await getSmsConversationThread(id, PAGE, null);
        // Polite announcement only for inbound that arrived since the last render, on a poll.
        const prev = seenSeqs.current;
        const fresh = prev
          ? t.items.filter((i) => i.kind === "inbound" && !prev.has(i.seq))
          : [];
        seenSeqs.current = new Set(t.items.map((i) => i.seq));
        // Announce new inbound on a poll AND after an action: a message that arrived while the operator was acting
        // (e.g. after the view loaded, before No reply needed) stays open and must be noticed (round-3 audit R3-H2).
        if ((mode === "poll" || mode === "action") && fresh.length > 0) {
          setAnnounce(
            fresh.length === 1
              ? "New message from this number."
              : `${fresh.length} new messages from this number.`,
          );
        }
        setThread(t);
        setState("ready");
        setLastUpdated(new Date());
        loadingRef.current = false;
        try {
          await ackSmsConversation(
            t.snapshot_id,
            mode === "initial" ? isForegroundView() : false,
          );
        } catch (e) {
          if (handleError(e) === "stale" && !retried) {
            await load(mode, true);
          }
        }
      } catch (e) {
        loadingRef.current = false;
        handleError(e);
      }
    },
    [id, handleError],
  );

  useEffect(() => {
    seenSeqs.current = null;
    setThread(null);
    setState("loading");
    void load("initial");
    const timer = window.setInterval(() => void load("poll"), POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const act = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    setNotice("");
    try {
      await fn();
      setNotice(done);
      void queryClient.invalidateQueries({
        queryKey: ["sms-conversation-list"],
      });
    } catch (e) {
      if (handleError(e) !== "stale") {
        setNotice("That didn't save. Try again.");
      }
    } finally {
      await load("action");
      setBusy(null);
    }
  };

  if (state === "withdrawn") {
    return <Navigate to="/dashboard" replace />;
  }

  const back = (
    <Link
      to="/messages"
      className="text-sm text-primary-600 hover:text-primary-800"
    >
      ← SMS inbox
    </Link>
  );

  if (state === "not_found") {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        {back}
        <p className="mt-6 text-sm text-gray-700">
          This conversation isn't available.
        </p>
      </div>
    );
  }

  if (state === "loading" || !thread) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        {back}
        {state === "error" ? (
          <div className="mt-6 text-sm">
            <p className="text-red-800">Messages couldn't load.</p>
            <button
              type="button"
              onClick={() => {
                setState("loading");
                void load("initial");
              }}
              className="mt-2 rounded border border-gray-300 px-3 py-1 text-sm text-gray-800 hover:bg-gray-50"
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-gray-600">Loading messages…</p>
        )}
      </div>
    );
  }

  const c = thread.conversation;
  const groups = groupByDay(thread.items);
  // Unanswered questions older than the loaded page (round-2 audit R2-M2): never hidden.
  const pageSeqs = new Set(thread.items.map((i) => i.seq));
  const earlierOpen = (thread.open_items ?? []).filter(
    (o) => !pageSeqs.has(o.seq),
  );
  const btn =
    "rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50";

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {back}
      <header className="mt-3 border-b border-gray-200 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-mono text-xl font-semibold text-gray-900">
            {c.member_masked}
          </h1>
          <SharedNumberChip count={thread.associations} />
          <span className="text-xs text-gray-600">
            {c.status === "closed" ? "Closed" : "Open"}
          </span>
        </div>
        {thread.suppressed && (
          <p className="mt-2 text-sm font-medium text-red-800">
            Sending blocked for this number after an opt-out.
          </p>
        )}
        {thread.review_incomplete && (
          <p className="mt-2 text-sm text-amber-900">
            More than 200 unanswered messages. The oldest 200 are listed below.
            Mark them No reply needed, then the next ones appear.
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={btn}
            disabled={!!busy}
            onClick={() =>
              void act(
                "unread",
                () => markSmsConversationUnread(c.id),
                "Marked unread.",
              )
            }
          >
            Mark unread
          </button>
          <button
            type="button"
            className={btn}
            disabled={!!busy}
            onClick={() =>
              void act(
                "read",
                () => markSmsConversationRead(c.id),
                "Marked read.",
              )
            }
          >
            Mark read
          </button>
          <button
            type="button"
            className={btn}
            disabled={!!busy}
            onClick={() =>
              void act(
                "nrn",
                () => markSmsConversationNoReplyNeeded(thread.snapshot_id),
                "Marked No reply needed.",
              )
            }
          >
            No reply needed
          </button>
          <span role="status" className="text-xs text-gray-700">
            {notice}
          </span>
        </div>
      </header>

      {earlierOpen.length > 0 && (
        <section
          aria-label="Unanswered earlier messages"
          className="mt-4 rounded border border-amber-300 bg-amber-50 p-3"
        >
          <h2 className="text-sm font-semibold text-amber-900">
            {earlierOpen.length === 1
              ? "1 unanswered message earlier in this conversation"
              : `${earlierOpen.length} unanswered messages earlier in this conversation`}
          </h2>
          <ul className="mt-2 space-y-2">
            {earlierOpen.map((o) => (
              <li key={o.seq} className="text-sm text-gray-900">
                <span className="text-xs text-gray-600">
                  {formatSmsTime(o.arrived_at)} ·{" "}
                </span>
                {o.body === null ? (
                  <em className="text-gray-600">
                    This message was removed under the retention policy.
                  </em>
                ) : (
                  <span className="whitespace-pre-wrap break-words">
                    {o.body}
                  </span>
                )}
                <span className="ml-2 rounded border border-red-300 bg-red-50 px-1 text-xs text-red-800">
                  Needs reply
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {thread.items.length === 0 ? (
        <p className="mt-6 text-sm text-gray-600">No messages yet.</p>
      ) : (
        <div
          role="log"
          aria-label="Messages"
          aria-live="off"
          className="mt-4 space-y-4"
        >
          {groups.map((g) => (
            <section key={g.key} aria-label={g.label}>
              <h2 className="mb-2 text-center text-xs font-medium text-gray-500">
                {g.label}
              </h2>
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <Message
                    key={it.seq}
                    item={it}
                    suppressed={thread.suppressed}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      {/* Polite announcement for newly arrived inbound only (§9.6); never for the initial load. */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announce}
      </div>

      <div className="mt-6 border-t border-gray-200 pt-3 text-sm">
        {(thread.reply_contacts ?? []).length > 0 && (
          <div>
            <p className="text-gray-700">
              Reply from the contact page. The reply appears here once it's
              sent. Replying there doesn't clear Needs reply. Use No reply
              needed after you reply.
            </p>
            <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {(thread.reply_contacts ?? []).map((c) => (
                <li key={c.contact_id}>
                  <Link
                    to={`/contacts/${c.contact_id}?sendSms=1`}
                    className="text-primary-600 hover:text-primary-800"
                  >
                    Reply as {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {(thread.restricted_contacts ?? 0) > 0 && (
          <p className="mt-1 text-xs text-gray-600">
            {thread.restricted_contacts} other linked{" "}
            {thread.restricted_contacts === 1 ? "record is" : "records are"}{" "}
            restricted.
          </p>
        )}
        {lastUpdated && (
          <p className="mt-2 text-xs text-gray-500">
            Last updated {formatSmsTime(lastUpdated.toISOString())}
          </p>
        )}
      </div>
    </div>
  );
};

export default SmsThread;
